import type React from 'react';
import { clsx } from 'clsx';

import { isString } from 'utils/check';
import type { LineLoaderProps } from 'ui';

import type { TextFieldProps } from './types';

interface AtomProps {
  className?: string;
  style?: React.CSSProperties;
}

type UseTextFieldView = (
  props: TextFieldProps,
  classes: Record<string, string>,
) => {
  rootProps: AtomProps;
  labelProps: AtomProps;
  labelTextProps: AtomProps;
  labelRequiredMarkerProps: AtomProps;
  inputContainerProps: AtomProps & { onClick?: TextFieldProps['fieldOnClick'] };
  inputProps: AtomProps & { placeholder?: string; disabled?: boolean };
  inputLockerProps: AtomProps;
  textareaProps: AtomProps & { placeholder?: string; disabled?: boolean };
  loaderProps: LineLoaderProps;
  startAdornmentProps: AtomProps;
  endAdornmentProps: AtomProps;
  helperTextProps: AtomProps;
  prefixProps: AtomProps;
  suffixProps: AtomProps;
};

export const useTextFieldView: UseTextFieldView = (
  {
    className,
    inputContainerClassName,
    inputClassName,
    label,
    suffix,
    prefix,
    placeholder,
    flexFullSize = false,
    widthFullSize = false,
    required = false,
    disabled = false,
    readOnly = false,
    size = 'md',
    shape = 'brick',
    highlight = 'non',
    startAdornment,
    endAdornment,
    helper,
    tag = 'input',
    fieldOnClick,
    input,
    type = 'text',
    width,
    valueOverflowEllipsis = false,
    adornmentsExtendHighlight = ['startAdornment'],
  },
  classes,
) => {
  const inputPlaceholder = placeholder ? `${placeholder}${required ? ' *' : ''}` : '';
  const inputDisabled = disabled || readOnly;

  return {
    rootProps: {
      className: clsx(className, classes.root),
      style: {
        flex: flexFullSize ? 1 : 'initial',
        width: widthFullSize ? '100%' : 'initial',
      },
    },
    labelProps: {
      className: clsx({
        [classes.label]: isString(label),
      }),
    },
    prefixProps: {
      className: clsx({
        [classes.prefix]: isString(prefix),
      }),
    },
    suffixProps: {
      className: clsx({
        [classes.suffix]: isString(suffix),
      }),
    },
    labelTextProps: {
      className: clsx({
        [classes.labelText]: isString(label),
      }),
    },
    labelRequiredMarkerProps: {
      className: clsx({
        [classes.requiredLabelMarker]: required,
      }),
    },
    inputContainerProps: {
      className: clsx(
        classes.inputContainer,
        {
          [classes.disabled]: disabled,
          [classes[`size-${size}`]]: size,
          [classes[`shape-${shape}`]]: shape,
          [classes[`highlight-${highlight}`]]: highlight !== 'non',
          [classes[`tag-${tag}`]]: tag,
          [classes.customInput]: input,
        },
        inputContainerClassName,
      ),
      onClick: fieldOnClick,
      style: { width: width ? `${width}px` : undefined },
    },
    inputProps:
      tag === 'input'
        ? {
            className: clsx(
              classes.input,
              {
                [classes.hasStartAdornment]: Boolean(startAdornment),
                [classes.hasEndAdornment]: Boolean(
                  endAdornment || disabled || type === 'password',
                ),
                [classes.valueOverflowEllipsis]: valueOverflowEllipsis,
              },
              inputClassName,
            ),
            placeholder: inputPlaceholder,
            disabled: inputDisabled,
          }
        : {},
    textareaProps:
      tag === 'textarea'
        ? {
            className: clsx(
              classes.input,
              classes.textarea,
              {
                [classes.hasStartAdornment]: Boolean(startAdornment),
                [classes.hasEndAdornment]: Boolean(
                  endAdornment || disabled || type === 'password',
                ),
              },
              inputClassName,
            ),
            placeholder: inputPlaceholder,
            disabled: inputDisabled,
          }
        : {},
    inputLockerProps: {
      className: clsx(classes.inputLocker, { [classes.active]: readOnly }),
    },
    loaderProps: {
      height: '2px',
      position: 'absolute',
    },
    startAdornmentProps: {
      className: clsx(classes.startAdornment, {
        [classes[`highlight-${highlight}`]]:
          adornmentsExtendHighlight.includes('startAdornment') && highlight !== 'non',
      }),
    },
    endAdornmentProps: {
      className: clsx(classes.endAdornment, {
        [classes.action]: type === 'password',
        [classes[`highlight-${highlight}`]]:
          adornmentsExtendHighlight.includes('endAdornment') && highlight !== 'non',
      }),
    },
    helperTextProps: {
      className: clsx({
        [classes.helperText]: helper && isString(helper),
        // [classes[`highlight-${highlight}`]]: highlight !== 'non',
      }),
    },
  };
};
